import { CihSectionHeader } from "~/components/cih/section-header";

type ContentCard = {
  key: string;
  typeLabel: string;
  title: string;
  description: string | null;
  href: string;
  cta: string;
  external?: boolean;
};

type LeadMagnetCard = {
  key: "lead-magnet";
  title: string;
  description: string | null;
  href: string;
  cta: string;
};

type Article = {
  slug: string;
  title: string;
  description: string | null;
  publishedAt: Date | null;
};

type Framework = {
  slug: string;
  /** `name` from CoachFrameworkBackRef — the display name of the framework. */
  name: string;
  description: string | null;
};

type LeadMagnet = {
  title: string | null;
  description: string | null;
  ctaText: string | null;
  url: string | null;
};

export type AlsoProps = {
  articles: Article[];
  frameworks: Framework[];
  leadMagnet: LeadMagnet | null;
  /** The coach's live Wheel of Life page; null hides its block. */
  wheelPageHref?: string | null;
  sectionNumber: number;
};

function pickLeadMagnetCard(
  leadMagnet: LeadMagnet | null,
): LeadMagnetCard | null {
  if (!leadMagnet?.title?.trim() || !leadMagnet.url?.trim()) return null;
  return {
    key: "lead-magnet",
    title: leadMagnet.title,
    description: leadMagnet.description,
    href: leadMagnet.url,
    cta: leadMagnet.ctaText?.trim() || "Download →",
  };
}

export function CoachAlsoSection({
  articles,
  frameworks,
  leadMagnet,
  wheelPageHref = null,
  sectionNumber,
}: AlsoProps) {
  const lead = pickLeadMagnetCard(leadMagnet);

  const contentCards: ContentCard[] = [];
  if (articles[0]) {
    const a = articles[0];
    contentCards.push({
      key: `article-${a.slug}`,
      typeLabel: "Article",
      title: a.title,
      description: a.description,
      href: `/articles/${a.slug}`,
      cta: "Read →",
    });
  }
  if (frameworks[0]) {
    const f = frameworks[0];
    contentCards.push({
      key: `framework-${f.slug}`,
      typeLabel: "Framework",
      title: f.name,
      description: f.description,
      href: `/frameworks/${f.slug}`,
      cta: "View →",
    });
  }

  // The assessment and the lead magnet always win a slot when present (they
  // are the conversion items), the assessment first because it needs no
  // sign-in. Content cards fight for the remaining slots, capped at 3 total.
  const fixedSlots = (wheelPageHref ? 1 : 0) + (lead ? 1 : 0);
  const cards = contentCards.slice(0, 3 - fixedSlots);

  if (fixedSlots === 0 && cards.length === 0) return null;

  const totalCards = fixedSlots + cards.length;
  const colsClass =
    totalCards === 1
      ? "md:grid-cols-1"
      : totalCards === 2
        ? "md:grid-cols-2"
        : "md:grid-cols-3";

  return (
    <section className="border-t border-gray-200">
      <CihSectionHeader
        label={`${String(sectionNumber).padStart(2, "0")} · Also from me`}
        title="More you can read & use"
      />
      <div className="mx-auto max-w-7xl px-4 pb-14 md:pb-20 xl:px-0">
        <div className={`grid gap-6 ${colsClass}`}>
          {wheelPageHref && <WheelPageTile href={wheelPageHref} />}
          {lead && <LeadMagnetTile lead={lead} />}
          {cards.map((c) => (
            <ContentTile key={c.key} card={c} />
          ))}
        </div>
      </div>
    </section>
  );
}

function ContentTile({ card }: { card: ContentCard }) {
  return (
    <a
      href={card.href}
      aria-label={`${card.typeLabel}: ${card.title}`}
      className="rounded-xl border border-gray-200 p-5 transition-colors hover:border-gray-300 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-600"
    >
      <div className="text-[10px] font-medium tracking-[0.16em] text-purple-700 uppercase">
        {card.typeLabel}
      </div>
      <h3 className="mt-2 font-heading text-base font-medium text-gray-900">
        {card.title}
      </h3>
      {card.description && (
        <p className="mt-2 line-clamp-2 text-sm text-gray-600">
          {card.description}
        </p>
      )}
      <span
        aria-hidden
        className="mt-3 inline-block text-xs font-medium text-purple-700 underline underline-offset-4"
      >
        {card.cta}
      </span>
    </a>
  );
}

function LeadMagnetTile({ lead }: { lead: LeadMagnetCard }) {
  return (
    <a
      href={lead.href}
      target="_blank"
      rel="noreferrer"
      aria-label={`Free guide: ${lead.title} (opens in new tab)`}
      className="group relative overflow-hidden rounded-xl border border-purple-200 bg-linear-to-br from-purple-50 to-purple-100/60 p-5 transition hover:border-purple-300 hover:shadow-sm focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-600"
    >
      <div className="flex items-center gap-2 text-[10px] font-medium tracking-[0.16em] text-purple-700 uppercase">
        <span aria-hidden className="size-1.5 rounded-full bg-purple-700" />
        Free guide
      </div>
      <h3 className="mt-2 font-heading text-base font-medium text-gray-900">
        {lead.title}
      </h3>
      {lead.description && (
        <p className="mt-2 line-clamp-2 text-sm text-gray-700">
          {lead.description}
        </p>
      )}
      <span
        aria-hidden
        className="mt-3 inline-block text-xs font-medium text-purple-700 underline underline-offset-4"
      >
        {lead.cta}
      </span>
    </a>
  );
}

function WheelPageTile({ href }: { href: string }) {
  return (
    <a
      href={href}
      aria-label="Take my assessment: Wheel of Life"
      className="group relative overflow-hidden rounded-xl border border-purple-200 bg-linear-to-br from-purple-50 to-purple-100/60 p-5 transition hover:border-purple-300 hover:shadow-sm focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-600"
    >
      <div className="flex items-center gap-2 text-[10px] font-medium tracking-[0.16em] text-purple-700 uppercase">
        <span aria-hidden className="size-1.5 rounded-full bg-purple-700" />
        Take my assessment
      </div>
      <h3 className="mt-2 font-heading text-base font-medium text-gray-900">
        Wheel of Life
      </h3>
      <p className="mt-2 line-clamp-2 text-sm text-gray-700">
        See how balanced your life is in three minutes.
      </p>
      <span
        aria-hidden
        className="mt-3 inline-block text-xs font-medium text-purple-700 underline underline-offset-4"
      >
        Start →
      </span>
    </a>
  );
}
